import { type ChangeEvent, type ComponentProps, type FC, useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';

export interface NumberInputProps extends Omit<ComponentProps<'input'>, 'value' | 'onChange'> {
  value: number | undefined;
  onChange?: (value: number) => void;
  /** Fired when the field is cleared. `onChange` only reports parsed numbers. */
  onEmpty?: () => void;
  min?: number;
  max?: number;
}

/**
 * Number input component that handles numeric values with optional min/max constraints.
 * Allows empty input and validates numeric input before invoking onChange.
 *
 * @returns JSX.Element
 */
export const NumberInput: FC<NumberInputProps> = ({ onChange, onEmpty, value, ...props }) => {
  const [dirtyValue, setDirtyValue] = useState<string | number | undefined>(value);

  useEffect(() => {
    setDirtyValue(value);
  }, [value]);

  const isValidNumber = (val: any) => {
    return typeof val === 'number' && !isNaN(val);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDirtyValue(newValue);

    if (newValue === '') {
      onEmpty?.();
      return;
    }

    const parsedNumber = Number.parseFloat(newValue);
    if (isValidNumber(parsedNumber)) {
      if (props.min !== undefined && parsedNumber < props.min) {
        setDirtyValue(props.min);
        onChange?.(props.min);
      } else if (props.max !== undefined && parsedNumber > props.max) {
        setDirtyValue(props.max);
        onChange?.(props.max);
      } else {
        onChange?.(parsedNumber);
      }
    }
  };

  // Convert undefined to empty string to empty input on undefined
  return (
    <Input
      {...props}
      // Override ui/input `dark:bg-input/30` → Figma `background/surface`.
      className={cn('dark:bg-background-surface', props.className)}
      onChange={handleChange}
      value={dirtyValue ?? ''}
      type="number"
    />
  );
};
